import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { groupsApi } from '../../api/groups';
import type { Group, Target } from '../../types';

interface GroupFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: Group | null;
}

interface FormState {
  name: string;
  recipients: string;
}

const emptyForm: FormState = {
  name: '',
  recipients: '',
};

function targetsToRecipients(targets: Target[]): string {
  return targets
    .map((t) => {
      const parts = [t.email || t.phone, ...(t.email && t.phone ? [t.phone] : []), t.first_name, t.last_name, t.position].filter(
        (p) => p && p.trim().length > 0,
      );
      return parts.join(', ');
    })
    .join('\n');
}

function toFormState(group: Group | null): FormState {
  if (!group) return emptyForm;
  return {
    name: group.name,
    recipients: targetsToRecipients(group.targets ?? []),
  };
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test(phone.trim());
}

function parseRecipients(text: string): Array<Omit<Target, 'id'>> {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const parts = line.split(',').map((part) => part.trim());
      const first = parts[0] ?? '';
      const hasEmail = first.includes('@');
      const phoneInSecondColumn = hasEmail && isValidPhone(parts[1] ?? '');
      const offset = hasEmail ? (phoneInSecondColumn ? 2 : 1) : 1;
      return {
        email: hasEmail ? first : '',
        phone: hasEmail ? (phoneInSecondColumn ? parts[1] : '') : first,
        first_name: parts[offset] ?? '',
        last_name: parts[offset + 1] ?? '',
        position: parts[offset + 2] ?? '',
      };
    });
}

export function GroupFormModal({ isOpen, onClose, group }: GroupFormModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={group ? `Edit Group — ${group.name}` : 'New Group'} size="md">
      <GroupForm key={group?.id ?? 'new'} group={group} onClose={onClose} />
    </Modal>
  );
}

function GroupForm({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => toFormState(group));
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (data: FormState) => {
      const targets = parseRecipients(data.recipients);
      const payload = { name: data.name.trim(), targets };
      if (group) {
        return groupsApi.update(group.id, payload);
      }
      return groupsApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save group');
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const name = form.name.trim();
    if (!name) {
      setError('Group name is required.');
      return;
    }

    const targets = parseRecipients(form.recipients);
    const invalid = targets.find((t) => {
      if (t.email && !isValidEmail(t.email)) return true;
      if (t.phone && !isValidPhone(t.phone)) return true;
      return !t.email && !t.phone;
    });
    if (invalid) {
      setError('Each recipient needs a valid email address or an E.164 phone number (for example, +14155550100).');
      return;
    }

    saveMutation.mutate(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="group-name">
            Group name
          </label>
          <input
            id="group-name"
            type="text"
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Finance team"
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="group-recipients">
            Recipient email addresses or phone numbers
          </label>
          <textarea
            id="group-recipients"
            className="input min-h-[160px]"
            value={form.recipients}
            onChange={(e) => setForm({ ...form, recipients: e.target.value })}
            placeholder="email@example.com, First, Last, Position or +14155550100, First, Last, Position"
            rows={6}
          />
          <p className="text-sm text-gray-500 mt-1">
            Enter one recipient per line: <code>email, firstName, lastName, position</code> or <code>+14155550100, firstName, lastName, position</code>. To keep both addresses, use <code>email, +14155550100, firstName, lastName, position</code>.
          </p>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      {saveMutation.isError && !error && (
        <p role="alert" className="text-sm text-red-600">
          Failed to save group.
        </p>
      )}

      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={saveMutation.isPending}>
          Save group
        </Button>
      </div>
    </form>
  );
}
